import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme
} from '@mui/material';
import {
  Architecture,
  Psychology,
  Science,
  Timeline,
  AutoFixHigh,
  CheckCircle,
  ContactMail,
  Lightbulb,
  Build,
  Verified
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { COLORS, withOpacity } from '../../theme/colors';
import SEOHead from '../../components/SEO/SEOHead';

const ThreeDDesignPrototyping = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: <Psychology />,
      title: 'Conceptual Design',
      description: 'Transform your ideas into detailed 3D concepts and visualizations'
    },
    {
      icon: <Architecture />,
      title: 'CAD Modeling',
      description: 'Professional 3D modeling using industry-standard CAD software'
    },
    {
      icon: <Science />,
      title: 'Prototyping',
      description: 'Rapid prototyping to test and validate your designs before production'
    },
    {
      icon: <AutoFixHigh />,
      title: 'Design Optimization',
      description: 'Refine and optimize designs for functionality and manufacturability'
    }
  ];

  const designProcess = [
    {
      title: 'Consultation',
      description: 'Discuss your project requirements, goals, and constraints with our design team.'
    },
    {
      title: 'Concept Development',
      description: 'Create initial concepts and sketches based on your specifications and feedback.'
    },
    {
      title: '3D Modeling',
      description: 'Develop detailed 3D models using professional CAD software for precision and accuracy.'
    },
    {
      title: 'Design Review',
      description: 'Review the design with you, make necessary adjustments, and finalize specifications.'
    },
    {
      title: 'Prototyping',
      description: 'Create physical prototypes using 3D printing or other manufacturing methods.'
    },
    {
      title: 'Final Delivery',
      description: 'Deliver final design files, documentation, and prototypes for your project.'
    }
  ];

  const serviceOfferings = [
    'Product Design and Development',
    'Mechanical Component Design',
    'Architectural Models and Visualization',
    'Custom Tool and Fixture Design',
    'Reverse Engineering',
    'Design for Manufacturing (DFM)',
    'Technical Documentation',
    'Prototype Testing and Validation'
  ];

  return (
    <>
      <SEOHead
        title="3D Design and Prototyping Services - Professional CAD Design | The Random Store"
        description="Professional 3D design and prototyping services. CAD modeling, product design, rapid prototyping, and design optimization. Transform your ideas into reality."
        keywords="3d design service, cad modeling, prototyping service, product design, 3d modeling india, rapid prototyping, design consultation, engineering design"
        url="https://therandomstores.com/services/3d-design-prototyping"
      />
      
      <Box sx={{ backgroundColor: COLORS.background.light, minHeight: '100vh', pb: 8 }}>
        {/* Hero Section */}
        <Box sx={{ 
          backgroundColor: COLORS.background.paper, 
          py: 8, 
          mb: 6,
          background: `linear-gradient(135deg, ${COLORS.secondary.main} 0%, ${COLORS.secondary.light} 100%)`,
          color: COLORS.common.white
        }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h2" fontWeight={700} gutterBottom>
                  3D Design & Prototyping
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, lineHeight: 1.6, mb: 4 }}>
                  From concept to creation - our expert design team helps you develop innovative products 
                  through professional 3D modeling, design optimization, and rapid prototyping services.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/contact')}
                    startIcon={<ContactMail />}
                    sx={{
                      backgroundColor: COLORS.common.white,
                      color: COLORS.secondary.main,
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: withOpacity(COLORS.common.white, 0.9)
                      }
                    }}
                  >
                    Start Project
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/services/3d-printing')}
                    sx={{
                      borderColor: COLORS.common.white,
                      color: COLORS.common.white,
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: COLORS.common.white,
                        backgroundColor: withOpacity(COLORS.common.white, 0.1)
                      }
                    }}
                  >
                    3D Printing Service
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 300
                }}>
                  <Architecture sx={{ fontSize: 150, opacity: 0.7 }} />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg">
          {/* Features Section */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" fontWeight={700} textAlign="center" mb={6} color={COLORS.text.primary}>
              Our Design & Prototyping Services
            </Typography>
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: `1px solid ${COLORS.border.light}`,
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${withOpacity(COLORS.secondary.main, 0.12)}`,
                        transform: 'translateY(-4px)',
                      }
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ 
                        backgroundColor: withOpacity(COLORS.secondary.main, 0.1),
                        borderRadius: '50%',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: COLORS.secondary.main
                      }}>
                        {React.cloneElement(feature.icon, { fontSize: 'large' })}
                      </Box>
                      <Typography variant="h6" fontWeight={600} gutterBottom color={COLORS.text.primary}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color={COLORS.text.secondary} sx={{ lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Process Section */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" fontWeight={700} textAlign="center" mb={6} color={COLORS.text.primary}>
              Our Design Process
            </Typography>
            <Grid container spacing={6}>
              <Grid item xs={12} md={8}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4,
                    backgroundColor: COLORS.background.paper,
                    border: `1px solid ${COLORS.border.light}`,
                    borderRadius: 3
                  }}
                >
                  <Stepper orientation="vertical">
                    {designProcess.map((step, index) => (
                      <Step key={step.title} active={true}>
                        <StepLabel
                          StepIconProps={{
                            style: { color: COLORS.secondary.main }
                          }}
                        >
                          <Typography variant="h6" fontWeight={600} color={COLORS.text.primary}>
                            {step.title}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography color={COLORS.text.secondary} sx={{ mb: 2, lineHeight: 1.6 }}>
                            {step.description}
                          </Typography>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4,
                    backgroundColor: COLORS.background.paper,
                    border: `1px solid ${COLORS.border.light}`,
                    borderRadius: 3,
                    height: 'fit-content',
                    position: 'sticky',
                    top: 20
                  }}
                >
                  <Typography variant="h5" fontWeight={700} gutterBottom color={COLORS.text.primary}>
                    What You Get
                  </Typography>
                  <List>
                    {[
                      'Professional 3D CAD files',
                      'Technical drawings',
                      'Design documentation',
                      'Prototype models',
                      'Design revisions',
                      'Manufacturing guidelines'
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: COLORS.secondary.main }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{
                            color: COLORS.text.primary,
                            fontWeight: 500
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => navigate('/contact')}
                    startIcon={<Lightbulb />}
                    sx={{
                      backgroundColor: COLORS.secondary.main,
                      fontWeight: 600,
                      py: 1.5,
                      mt: 3,
                      '&:hover': {
                        backgroundColor: COLORS.secondary.dark
                      }
                    }}
                  >
                    Discuss Your Project
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Service Offerings */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 4,
              backgroundColor: COLORS.background.paper,
              border: `1px solid ${COLORS.border.light}`,
              borderRadius: 3
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom color={COLORS.text.primary} textAlign="center">
              Service Offerings
            </Typography>
            <Typography variant="body1" color={COLORS.text.secondary} sx={{ mb: 4, textAlign: 'center', lineHeight: 1.6 }}>
              We provide comprehensive design and prototyping services across various industries and applications.
            </Typography>
            <Grid container spacing={2}>
              {serviceOfferings.map((service, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: withOpacity(COLORS.secondary.main, 0.05)
                    }
                  }}>
                    <Verified sx={{ color: COLORS.secondary.main, mr: 1.5, fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500} color={COLORS.text.primary}>
                      {service}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default ThreeDDesignPrototyping;
